import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Hero } from '../../widgets/Hero';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import { Summary } from '../../widgets/Summary';

import * as css from './SoftwareArchitecture.m.css';
import Head from '../../widgets/Head';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import SummaryList from '../../widgets/SummaryList';
import WhySitePen from '../../widgets/WhySitePen';

const analysisImg = require('../../assets/images/illustration/analysis.svg');
const backEndServicesImg = require('../../assets/images/icon-illustration/back-end-services.svg');
const dataImg = require('../../assets/images/illustration/data.svg');
const databaseTechnologyImg = require('../../assets/images/icon-illustration/database-technology.svg');
const designImg = require('../../assets/images/illustration/design.svg');
const frontEndToolsImg = require('../../assets/images/icon-illustration/front-end-tools.svg');
const integrationImg = require('../../assets/images/icon-illustration/enterprise-application-integration.svg');
const modernizationImg = require('../../assets/images/icon-illustration/legacy-application-modernization.svg');

const factory = create();

export const SoftwareArchitecture = factory(function SoftwareArchitecture() {
	return (
		<div classes={css.root}>
			<Head
				title="Enterprise Software Architecture Design | SitePen"
				description="SitePen offers enterprise software architecture design as part of our development offerings. These services are part of our mission to improve processes and operations for businesses."
			/>

			<Hero background="level3Pink">
				{{
					xlTitle: (
						<div>
							Software
							<br />
							Architecture
						</div>
					)
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<Summary reverse collapse="large">
						{{
							title: 'Software Designed for Your Business',
							blurb: (
								<virtual>
									<p>
										Today many organizations are software companies, whether
										they acknowledge it or not. Modern commerce is at a point
										where software is in most cases required to conduct business
										transactions - and many companies sell software products as
										their primary line of business. Small companies can often
										simply use off-the-shelf software solutions for their needs;
										however, large organizations typically implement internal
										solutions to retain control over the full customer
										engagement lifecycle.
									</p>
									<p>
										Well-architected and well-designed software should appear to
										take a step back from the purpose it serves, almost becoming
										invisible. A user of such a software system should perform
										the function they need to without the system hindering their
										process. Reaching this point of seamless accessibility and
										function is challenging, but attempting to do so means
										businesses can significantly enhance user satisfaction,
										customer loyalty, spending, and reach.
									</p>
								</virtual>
							),
							image: <Image src={analysisImg} size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section type="leading">
				<ContentWrapper>
					<Summary collapse="large">
						{{
							title: 'Identify Flaws Early',
							blurb: (
								<virtual>
									<p>
										Many types of flaws arise during the implementation phase of
										a software system. Diligence and additional processes are
										required during development to minimize the incidence and
										severity of such defects. Today this typically means
										constant testing, iteration, and refinement throughout the
										implementation of a software product. Functional flaws
										generally are allocated the most considerable attention for
										fixing; however, many other types of faults can remain - and
										may have a significant negative impact on user satisfaction.
										These include usability flaws, having users perform
										convoluted journeys to complete simple functions;
										performance and scalability issues, where the system cannot
										meet user demand; or most severely, security issues, where
										confidentiality of customers and their data is compromised.
									</p>
									<p>
										Flaws are cheaper to resolve the earlier they are
										identified. Modern software development practices encourage
										ongoing, iterative identification and resolution; however,
										it is also vital to conduct sufficient up-front architecture
										and design work to avoid as many issues as possible ahead of
										time. The “best” flaw is one that is purposefully never
										encountered, where development time is not wasted in
										reaching it, backing out a flawed implementation, nor
										finding a way to resolve or work around it. Establishing
										robust software architecture practices as part of a broader
										software product delivery lifecycle can significantly help
										mitigate flaws ahead of time and reduce the impact of any
										flaws that are encountered.
									</p>
								</virtual>
							),
							image: <Image src={designImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={dataImg} lazy size="lg" />,
							title: 'What Is Software Architecture Design?',
							blurb: (
								<virtual>
									<p>
										Similar to physical buildings, an architecture design is a
										blueprint for building and operating a software system.
										Architecture should identify a system’s major components,
										the responsibility that each piece fulfills (how areas map
										to business processes), the data required within each area,
										how communication happens across boundaries, and how each
										part should be deployed and scaled. Every system has such a
										blueprint; however, some systems may only have an implicit
										blueprint if their architecture is not documented externally
										to the software implementation itself. For simple products,
										an implicit architecture may be enough. As systems increase
										in scope and size, the importance of having a
										well-documented architecture blueprint grows.
									</p>
									<p>
										Good software architecture should identify the logical and
										physical layouts of the system components. If produced ahead
										of time, the system’s implementation can use the
										architecture design as a template for exactly what to build.
										However, in the real world, where requirements and
										technologies are continually evolving, it is next to
										impossible to produce a perfect architecture at the start of
										development. Software architecture differs from traditional
										architecture in this way - software design and
										implementation need to adapt over time to meet ever-changing
										needs from the business, customers, and the broader
										technology industry.
									</p>
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<WhySitePen>
						{{
							blurb:
								'SitePen has helped organizations design, implement, and deliver successful software systems for many years across various industries and scales. Our team comprises a wealth of expertise from first-stage startups through to the largest multinational enterprise corporations. We can help in every phase of the software product lifecycle, including requirements analysis, enterprise and software architecture, user experience and interface design, system implementation, infrastructure planning, and delivery to end-users. Our team can help as an integrated part of your internal delivery capability or operate as a standalone end-to-end solution delivery function. Our architecture services range from broad-brush enterprise strategy down to individual product design and implementation.'
						}}
					</WhySitePen>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'Software Architecture Services',
							blurb: (
								<virtual>
									We offer a variety of architecture services to fit your specific
									business needs
								</virtual>
							),
							items: [
								{
									title: 'Consulting, Evaluation & Analysis',
									blurb:
										'Whether you are looking to implement a new software solution, review an established system to meet new goals, or just find the best way technology can help target specific opportunities, SitePen can help. We can assess your current or future software ecosystem against your intended market and product roadmap to find the best marriage between technology and business. Our approach to software architecture and surrounding analysis is firmly rooted in pragmatism and encourages iterative delivery that continuously achieves success throughout a project’s lifespan.',
									image: (
										<Image
											src={databaseTechnologyImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Software Architecture Analysis Method',
									blurb:
										'Our process involves working with all relevant product stakeholders to gather all pertinent requirements across areas such as business functionality, security, integration, platform capabilities, capacity projections, infrastructure needs, operational concerns, timeframes, budget, and any other pertinent organizational policies. This requirements analysis forms the basis for future software design and implementation phases. When assessing an existing system, the process factors in the current implementation and its usage. To more effectively meet new business needs, ways to adapt, extend, or possibly replace the system are analyzed.',
									image: (
										<Image
											src={integrationImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Software Architecture Design',
									blurb:
										'With a solid foundation of requirements and assumptions established and agreed upon, SitePen can design an architecture blueprint that enables delivering a successful product that best meets all needs. This phase is a natural continuation of the analysis phase together with full stakeholder engagement. Our goal with architecture design at SitePen is to deliver robust blueprints that are achievable given any necessary compromises and not ivory tower concepts that are unattainable. This phase may also include proofs-of-concept where a choice between multiple optimal solutions may exist. Proof-of-concept validation before investing in a full implementation effort can also be useful in proving an intended direction will adequately meet requirements.',
									image: (
										<Image
											src={frontEndToolsImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Software Architecture Implementation',
									blurb:
										'With an architecture established, SitePen will advocate the design amongst project stakeholders to ensure everyone is in alignment with the intended direction. Having the decision points and rationale behind the choices made in defining the architecture accessible to everyone involved is key to fostering cohesive understanding while minimizing confusion. When dealing with technology teams, this includes overseeing adherence to the architecture throughout implementation while also considering any implementation, testing, deployment, or other challenges that may arise. If encountering any such issues, SitePen will adapt or extend the architecture as needed to best address the problem and move forward with minimal project impact. Where desired, SitePen can also directly take on the software implementation, whether as part of your existing delivery teams or as a completely standalone delivery function.',
									image: (
										<Image
											src={backEndServicesImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Infrastructure Analysis',
									blurb:
										'Understanding capacity requirements and ensuring efficient infrastructure usage is equally important to the success of a software system. SitePen can help analyze software component runtime requirements and expected usage projections to design an efficient and cost-effective infrastructure hosting topology that is flexible enough to meet user demand changes. Our expertise covers both internal	on-premise infrastructure as well as storage and compute cloud technologies from a range of third party vendors.',
									image: (
										<Image
											src={modernizationImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section>
				<IndustryExpertise>
					{{
						title: 'Industries We Serve',
						content:
							'At SitePen, we have applied our expertise across a wide range of industries and solution scales. We have worked on products for financial services, technology (proprietary and open-source), public services, hospitality, and recreation industries, amongst others. Our work includes discrete business-to-customer and business-to-business solutions and internal cross-cutting concerns such as technology strategy setting, requirements analysis, user experience & user interface design, software and cloud infrastructure design, migration, uplift, and software solution implementations.'
					}}
				</IndustryExpertise>
			</Section>
			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="Software Architecture FAQs">
					{[
						{
							title: 'How Long Does Architecture Analysis and Design Take?',
							content: (
								<p>
									When conducted well, architecture analysis and design should
									occur throughout the full lifecycle of a software system.
									However, this does not mean it requires 100% allocation
									throughout a project’s timeline - the critical point where
									architecture is most required is part of project ideation and
									early implementation. The pillars laid out as part of a software
									system’s architecture can significantly impact the direction and
									cost of full implementation, so they are best established before
									implementation occurs. In this way, architecture is most
									effective when it is “shifted left” or “shifted early” in the
									project plan. The need for constant architecture input should
									naturally taper off as implementation stabilizes.
								</p>
							)
						},
						{
							title: 'Why Is Architecture Analysis and Design Important?',
							content: (
								<virtual>
									<p>
										Software implementations that do not cater to some form of
										architecture design, planning, and review will typically
										require significantly higher investment to achieve success.
										Releasing a system in an unknowingly-compromised state
										forces businesses to endure a loss of customer confidence
										and reduced revenue. This extra hidden cost comes in through
										several overlapping angles - wasted effort implementing
										fundamentally flawed paths, backtracking and rewriting areas
										to achieve stated goals, as well as losses in business and
										customer confidence through insufficient capacity planning.
									</p>
									<p>
										Having a robust architecture process as part of your
										software implementation lifecycle can significantly mitigate
										these risks, allowing you to deliver successful products
										with minimal issues.
									</p>
								</virtual>
							)
						},
						{
							title: 'What Makes Good Software Architecture?',
							content: (
								<p>
									Good software architecture gets out of the way and appears
									invisible. Users can perform their tasks efficiently and conduct
									their business without impediments in a well-architected system.
									Good software architecture should support the business’s goals
									and the system’s users while being as efficient with resources
									as possible. Well-architected systems should scale to meet a
									wide range of user demands without overprovisioning unnecessary
									software and infrastructure. Well-architected systems should
									also be secure, providing robust protection for user and
									information confidentiality that conform with relevant regional
									operational requirements.
								</p>
							)
						},
						{
							title: 'Who Can Benefit From Architecture Analysis?',
							content: (
								<p>
									Architecture analysis can help all organizations that deal with
									bespoke software systems, whether they have internal technology
									teams that handle the software’s implementation or contract out
									to third party delivery vendors. These firms rely on custom
									software systems to conduct their business and significantly
									benefit from architecture analysis and assessment and design and
									implementation support. Ideally, architectural oversight would
									happen before and during the implementation of a new system.
									However, they can also provide benefits when done as a
									post-completion review. While some systems may prove adequately
									successful with their initial launch, over time, changes in
									industry, market, and user expectations may necessitate further
									architecture work. Operational systems may need to be further
									analyzed to see how they can be extended, improved, or
									potentially replaced to more effectively meet current
									requirements.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default SoftwareArchitecture;
